import { useEffect, useRef, useState } from "react";
import { PdfGenerator } from "@/components/PdfGenerator";
import PdfViewDrawer from "@/components/PdfViewDrawer";
import { useLoadingContext } from "@/context/LoadingProvider";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import GroupSessionNoteTemplate from "@/template/groupSessionNoteTemplate/GroupSessionNoteTemplate";
import {
  GROUP_SESSION_PRINT_FILE_NAME,
  GROUP_SESSION_PRINT_LABEL,
  SESSION_NOTE_PRINT_DRAWER_WIDTH,
} from "../group-visit-constant";
import { GroupSessionPrintData } from "../group-visit-types";

export type GroupSessionNotePrintDrawerProps = {
  disclosure: DisclosureHost;
  /** The session and its shared note, already read by the screen. */
  data: GroupSessionPrintData;
};

/**
 * The shared group note as a PDF, shown in the same side drawer the individual
 * visit note prints into.
 *
 * @author Rajkumar Rathod
 */
const GroupSessionNotePrintDrawer = ({
  disclosure,
  data,
}: GroupSessionNotePrintDrawerProps) => {
  const pdfRef = useRef<{ generatePdf: (name?: string) => void }>(null);
  const { setIsLoading } = useLoadingContext();
  const [previewFile, setPreviewFile] = useState<string>("");
  const printViewDisclosure = useDisclosure(false, {
    onClose: disclosure.close,
  });

  const hasGeneratedRef = useRef<boolean>(false);

  // `setIsLoading` changes identity on every provider render, so guard to generate once per open.
  useEffect(() => {
    if (!disclosure.opened) {
      hasGeneratedRef.current = false;
      return;
    }
    if (hasGeneratedRef.current) return;
    hasGeneratedRef.current = true;
    setIsLoading(true);
    pdfRef.current?.generatePdf(GROUP_SESSION_PRINT_FILE_NAME);
  }, [disclosure.opened, setIsLoading]);

  if (!disclosure.opened) return null;

  return (
    <>
      <PdfGenerator
        ref={pdfRef}
        onSave={(blob) => {
          setIsLoading(false);
          if (!blob) return;
          setPreviewFile(window.URL.createObjectURL(blob));
          printViewDisclosure.open();
        }}
      >
        <GroupSessionNoteTemplate data={data} />
      </PdfGenerator>

      <PdfViewDrawer
        w={SESSION_NOTE_PRINT_DRAWER_WIDTH}
        file={previewFile}
        disclosureHost={printViewDisclosure}
        title={GROUP_SESSION_PRINT_LABEL.drawerTitle}
      />
    </>
  );
};

export default GroupSessionNotePrintDrawer;
